import {
  useFieldUpdater,
  useListingField,
  useListingStore,
} from '@brightmls/nextgen.lae.flc';
import { useState } from 'react';
import { useSidePanel } from '../../../../contexts/SidePanelContext';
import { ListingResource, PropertyField } from '../../../../system/field';
import { SearchAgentResult } from '../../../../system/search';
import { AgentForm } from '../../../common/form/AgentForm';
import { agentFieldMapping } from './constants';
import { MemberDefaults } from '../../../../system/defaults';

export const useAgents = () => {
  const { showPanel, hidePanel } = useSidePanel();
  const updateFields = useFieldUpdater(ListingResource.Property);
  const [agentData, setAgentData] = useState<{
    list: SearchAgentResult | null;
    coList: SearchAgentResult | null;
    buyer: SearchAgentResult | null;
    coBuyer: SearchAgentResult | null;
  }>({
    list: null,
    coList: null,
    buyer: null,
    coBuyer: null,
  });

  // Field subscriptions
  const comparableYN = useListingField(
    ListingResource.Property,
    PropertyField.COMPARABLE_YN,
    false,
  );

  const stateOrProvince = useListingField(
    ListingResource.Property,
    PropertyField.STATE_OR_PROVINCE,
    '',
  );

  // Comparable is based on the field value
  const isComparable = Boolean(comparableYN.value);

  const getAgentData = (prefix: string): SearchAgentResult | undefined => {
    // This is a bit tricky with FLC - we need to read multiple fields at once
    // For now, we'll use the store directly for this read operation
    const state = useListingStore.getState();

    const agentData: Partial<SearchAgentResult> = {};
    Object.entries(agentFieldMapping).forEach(([field, key]) => {
      const fieldKey = prefix + field.slice(5);
      agentData[key as keyof SearchAgentResult] =
        state[ListingResource.Property][fieldKey] || '';
    });
    return Object.values(agentData).some(Boolean)
      ? (agentData as SearchAgentResult)
      : undefined;
  };

  const getCoListAgentDataFromDefaults = (
    defaults: MemberDefaults,
  ): SearchAgentResult | undefined => {
    const agentData: Partial<SearchAgentResult> = {};
    Object.entries(agentFieldMapping).forEach(([field, key]) => {
      const fieldKey = 'CO_LIST_' + field.slice(5);
      //@ts-ignore - to do: fix this type issue below
      agentData[key as keyof SearchAgentResult] = defaults[fieldKey] || '';
    });
    return Object.values(agentData).some(Boolean)
      ? (agentData as SearchAgentResult)
      : undefined;
  };

  const updateAgent = (prefix: string, agent: SearchAgentResult) => {
    const updates: Record<string, any> = {};

    Object.entries(agentFieldMapping).forEach(([field, key]) => {
      const fieldKey = prefix + field.slice(5);
      updates[fieldKey] = agent[key as keyof SearchAgentResult];
    });

    updateFields(updates);
  };

  const handleAddAgent = (
    prefix: string,
    title: string,
    agentType: 'List' | 'Co-List' | 'Selling' | 'Co-Selling',
  ) => {
    // Prevent editing for comparable listings
    if (isComparable && (prefix === 'LIST_' || prefix === 'CO_LIST_')) {
      return;
    }

    const initialAgent = getAgentData(prefix);
    showPanel(
      title,
      <AgentForm
        onSave={(agent) => {
          updateAgent(prefix, agent);
          hidePanel();
        }}
        initialAgent={initialAgent}
        isScopedToBrokerage={agentType === 'List'}
        agentType={agentType}
        listingStateOrProvince={stateOrProvince.value}
      />,
    );
  };

  // Pick back up here
  const handleDeleteAgent = (prefix: string) => {
    // Prevent deleting for comparable listings
    if (isComparable && (prefix === 'LIST_' || prefix === 'CO_LIST_')) {
      return;
    }

    const clearUpdates: Record<string, null> = {};
    Object.keys(agentFieldMapping).forEach((field) => {
      clearUpdates[prefix + field.slice(5)] = null;
    });
    updateFields(clearUpdates);

    // Clear the corresponding agent data
    let agentType: keyof typeof agentData;

    switch (prefix) {
      case 'LIST_':
        agentType = 'list';
        break;
      case 'CO_LIST_':
        agentType = 'coList';
        break;
      case 'BUYER_':
        agentType = 'buyer';
        break;
      case 'CO_BUYER_':
        agentType = 'coBuyer';
        break;
      default:
        console.error(`Unknown prefix: ${prefix}`);
        return;
    }

    setAgentData((prev) => ({
      ...prev,
      [agentType]: null,
    }));
  };

  return {
    agentData,
    isComparable,
    stateOrProvince,
    setAgentData,
    handleAddAgent,
    updateAgent,
    handleDeleteAgent,
    getCoListAgentDataFromDefaults,
  };
};
